/**
 * Shadow of Volto core SearchBlock SearchInput.
 *
 * Two SearchBlock roles share this component in the PRC frontend:
 *   • the faceted BROWSE pages (Documents, presets) — facets + per-column sort
 *     only, NO free-text box (browse is filter-driven);
 *   • the Solr-backed SEARCH APP at /search (block id "search-app") — a full
 *     text search that MUST expose a prominent query box.
 *
 * The block id is the discriminator (SearchBlockView threads props.id down to
 * SearchInput). For the Search App we render the real input; for every browse
 * block we render nothing, preserving the original "hide the box on browse"
 * behaviour without having to set showSearchInput:false on each browse block.
 *
 * The Search-App branch is a copy of core SearchInput.jsx (Volto 19.1.5) so it
 * keeps the exact live-search / clear wiring the block expects, plus the
 * typeahead described below.
 *
 * ── TYPEAHEAD (item 3) ────────────────────────────────────────────────────
 *
 * The panel suggests DOCUMENTS to jump to, not query completions. Someone
 * typing "DOE-STD-1104" in this archive already knows what they want; handing
 * them the document is a shorter path than handing them a better-spelled query
 * and making them run it. Enter on a highlighted row navigates; Enter on
 * nothing runs the ordinary search, exactly as before — the typeahead is
 * strictly additive, and the box behaves identically if it never appears.
 *
 * The whole feature degrades to nothing on failure: no suggestions renders no
 * panel, and the endpoint answers an empty list rather than an error when Solr
 * is unreachable.
 *
 * Keyboard/ARIA follow the ARIA 1.2 combobox pattern — focus stays in the
 * input at all times and `aria-activedescendant` tracks the highlighted row.
 * See components/Search/SuggestPanel.jsx.
 */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Input } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import loupeSVG from '@plone/volto/icons/zoom.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

import useSuggest from '../../../../../../helpers/useSuggest';
import SuggestPanel from '../../../../../../components/Search/SuggestPanel';

const SEARCH_APP_ID = 'search-app';

const messages = defineMessages({
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  cancel_search: {
    id: 'Cancel search',
    defaultMessage: 'Cancel search',
  },
});

const isAbsolute = (url) => /^https?:\/\//i.test(url || '');

const SearchInput = (props) => {
  const {
    id,
    data,
    searchText,
    setSearchText,
    isLive,
    onTriggerSearch,
    removeSearchQuery,
    facets,
  } = props;
  const intl = useIntl();
  const history = useHistory();

  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  // Suggestions follow the Archive facet rather than inventing a second
  // scoping vocabulary: whatever archives the results page is searching, the
  // typeahead searches too. Anything else and the panel could offer a document
  // that the search behind it would not return — including the case that makes
  // this worth spelling out, where someone on THIS frontend has filtered the
  // results to a sibling archive and the panel would otherwise keep offering
  // this one. No facet means the current archive, which is the endpoint's
  // default, so nothing is sent.
  const siteFacet = facets?.site;
  const sites = React.useMemo(
    () =>
      (Array.isArray(siteFacet) ? siteFacet : [siteFacet])
        .map((v) => (typeof v === 'string' ? v : v?.value ?? v?.token))
        .filter(Boolean),
    [siteFacet],
  );
  // A comma list, not the array: `scope` is an effect dependency in useSuggest
  // and a string compares by value, so a facet array rebuilt on every render
  // cannot make the request fire again.
  const scope = sites.length ? sites.join(',') : undefined;
  const multiSite = sites.length > 1;

  const isSearchApp = id === SEARCH_APP_ID;
  const { items, term } = useSuggest(searchText, {
    scope,
    enabled: isSearchApp && open,
  });

  // Only offer rows that describe what is currently typed. Without this the
  // panel briefly shows the previous prefix's matches under a newer query.
  const suggestions = term === (searchText || '').trim() ? items : [];

  // A new query makes the old highlight meaningless — and worse, leaves it
  // pointing at a row that is now a different document.
  React.useEffect(() => setActiveIndex(-1), [searchText]);

  const close = React.useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const pick = React.useCallback(
    (item) => {
      close();
      if (!item?.url) return;
      // Cross-archive suggestions carry an absolute URL on another domain,
      // which the router cannot route to.
      if (isAbsolute(item.url)) window.location.assign(item.url);
      else history.push(item.url);
    },
    [close, history],
  );

  const onKeyDown = (event) => {
    const count = suggestions.length;
    switch (event.key) {
      case 'ArrowDown':
        if (!count) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((i) => (i + 1) % count);
        return;
      case 'ArrowUp':
        if (!count) return;
        event.preventDefault();
        setActiveIndex((i) => (i <= 0 ? count - 1 : i - 1));
        return;
      case 'Enter':
        if (activeIndex >= 0 && suggestions[activeIndex]) {
          // Jump to the document instead of searching for its name.
          event.preventDefault();
          pick(suggestions[activeIndex]);
          return;
        }
        close();
        onTriggerSearch(searchText);
        return;
      case 'Escape':
        // First Escape dismisses the panel and KEEPS what was typed, so a
        // second Escape is the browser's own "clear the field" and still does
        // what the user expects.
        if (open) event.preventDefault();
        close();
        return;
      case 'Tab':
        close();
        return;
      default:
    }
  };

  // Browse blocks keep the original behaviour: no free-text box.
  if (!isSearchApp) return null;

  const listboxId = `${id}-suggestions`;
  const panelOpen = open && suggestions.length > 0;

  return (
    <div
      className="search-input"
      role="combobox"
      aria-expanded={panelOpen}
      aria-owns={panelOpen ? listboxId : undefined}
      aria-haspopup="listbox"
    >
      <Input
        id={`${id}-searchtext`}
        value={searchText}
        placeholder={
          data.searchInputPrompt || intl.formatMessage(messages.search)
        }
        // BL-0157 (TAIUIT quality/form-labels): a placeholder is not an
        // accessible name. Same expression as the placeholder; semantic-ui's
        // Input forwards aria-* props to the inner <input>.
        aria-label={
          data.searchInputPrompt || intl.formatMessage(messages.search)
        }
        fluid
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={panelOpen ? listboxId : undefined}
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        // Rows commit on mouseDown (see SuggestPanel), which runs before blur,
        // so closing here cannot swallow a click.
        onBlur={close}
        onKeyPress={(event) => {
          // Enter is fully handled in onKeyDown — including the case where it
          // must NOT search, because a suggestion is highlighted. Letting the
          // original handler fire here too would run the search anyway.
          if (event.key === 'Enter') return;
          if (isLive) onTriggerSearch(searchText);
        }}
        onChange={(event, { value }) => {
          setSearchText(value);
          setOpen(true);
          if (isLive) {
            onTriggerSearch(value);
          }
        }}
      />

      <SuggestPanel
        id={listboxId}
        items={panelOpen ? suggestions : []}
        term={searchText}
        activeIndex={activeIndex}
        onPick={pick}
        showSite={multiSite}
      />

      <div className="search-input-actions">
        {searchText && (
          <Button
            type="button"
            basic
            icon
            className="search-input-clear-icon-button"
            onClick={() => {
              setSearchText('');
              removeSearchQuery();
              close();
            }}
            aria-label={intl.formatMessage(messages.cancel_search)}
          >
            <Icon name={clearSVG} />
          </Button>
        )}
        {isLive && (
          <>
            <div className="divider" />
            <Button
              basic
              icon
              className="search-input-live-icon-button"
              aria-label={`${intl.formatMessage(messages.search)} ${searchText}`}
            >
              <Icon name={loupeSVG} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchInput;
